import { useEffect, useRef } from "react";
import { getOrFetchTileToken } from "../api/tileTokens";
import { tileUrl as buildTileUrl } from "../api/tileUrl";
import { useLayerRegistryStore } from "../stores/useLayerRegistryStore";
import { copernicusAttribution } from "../lib/map/attribution";
import { isLiveMap } from "../lib/map/liveMap";
import { FOOTPRINT_OVER_ANCHOR } from "./useLayerStackManager";

// Which FeatureCollection each inline source currently holds, per map.
//
// A geojson-inline source is created once and then never written again: the
// user-layer branch below returns early as soon as the maplibre layer exists,
// and syncGeoJsonLayer only calls addSource when the source is absent. That was
// fine while a saved layer's content never changed. It stopped being fine when
// layers became editable — the PATCH succeeded, the registry held the new
// FeatureCollection, and the map went on drawing the one it was first given.
//
// Identity is the revision marker rather than a timestamp: the row object is
// replaced wholesale whenever it is re-fetched or PATCHed, so a new `geojson`
// reference means new content, and an unchanged reference means there is
// nothing to push. It needs no cooperation from the database, which matters
// because nothing here can guarantee updated_at moves on every write.
//
// WeakMap keyed by map so the two compare panes keep separate bookkeeping and
// neither pins the other's sources alive.
const appliedSourceData = new WeakMap();

function sourceRevisions(map) {
    let m = appliedSourceData.get(map);
    if (!m) { m = new Map(); appliedSourceData.set(map, m); }
    return m;
}

function idsFor(layerId) {
    const safeId = layerId.replace(/[:]/g, "_");
    return {
        sourceId: `src-${safeId}`,
        layerId: `lyr-${safeId}`
    };
}

// ── ONE SOURCE PER DATE, NOT ONE PER LAYER ─────────────────────────────────
//
// Imagery used to get a single MapLibre source per registry entry, reused
// across dates by calling setTiles() with a new URL. setTiles INVALIDATES the
// source: every cached tile is dropped and the layer paints nothing until the
// replacements arrive. That is the blank flash between acquisitions, and it
// happens on a plain click as much as during play.
//
// raster-fade-duration does not help — it cross-fades tiles WITHIN a source
// across zoom levels, not across a URL swap.
//
// So the source is keyed by (registry entry, dataset_key). Switching dates then
// means showing a different source rather than rebuilding one, and the tiles of
// the date you just left are still in memory when you come back.
//
// MAPLIBRE CANNOT REPOINT A LAYER AT A DIFFERENT SOURCE, so the layer has to be
// keyed the same way. That makes this a genuine double-buffer: the incoming
// layer is added while the outgoing one is still visible, and the outgoing one
// is hidden only once the incoming source reports loaded. Until then the new
// layer simply has no tiles and the old one shows through, which is exactly the
// behaviour we want and is why the new layer goes on TOP.
//
// RETENTION IS BOUNDED. Keeping every date visited would grow without limit
// during play; keeping only the current one would reload on every step and give
// back the flash. Hidden variants beyond the most recent RETAINED_DATES are
// removed outright, so a play loop over the dates that actually cover the view
// — which is all play steps through, see ExploreTimeStrip — is instant after one pass.
const RETAINED_DATES = 4;

export function imageryIdsFor(registryId, datasetKey) {
    const safeId = String(registryId).replace(/[:]/g, "_");
    // dataset keys are `imagery.permian.midland.2025_12_13`; dots are legal in
    // MapLibre ids but make the ids hard to read in the inspector.
    const safeKey = String(datasetKey).replace(/[^A-Za-z0-9_-]/g, "_");
    return {
        sourceId: `src-${safeId}--${safeKey}`,
        layerId: `lyr-${safeId}--${safeKey}`
    };
}

// registryId -> dataset keys mounted for it, least-recently-shown first.
const imageryVariants = new WeakMap();
function variantsFor(map) {
    let m = imageryVariants.get(map);
    if (!m) { m = new Map(); imageryVariants.set(map, m); }
    return m;
}

/** Remove every mounted variant of one registry entry. Used on unmount. */
function removeAllImageryVariants(map, registryId) {
    const all = variantsFor(map);
    for (const key of all.get(registryId) ?? []) {
        const { sourceId, layerId } = imageryIdsFor(registryId, key);
        if (map.getLayer(layerId)) map.removeLayer(layerId);
        if (map.getSource(sourceId)) map.removeSource(sourceId);
    }
    all.delete(registryId);
    // The pre-variant id shape, for a layer mounted before this change and
    // still on the map across a hot reload. Cheap, and prevents an orphan that
    // nothing would ever remove.
    const legacy = idsFor(registryId);
    if (map.getLayer(legacy.layerId)) map.removeLayer(legacy.layerId);
    if (map.getSource(legacy.sourceId)) map.removeSource(legacy.sourceId);
}

/**
 * The MapLibre layer id currently showing for a registry entry.
 *
 * EXPORTED BECAUSE THE ID SHAPE IS NO LONGER GUESSABLE. Callers used to build
 * `lyr-${layer_id}` by hand — flicker mode did, in four places — and that id
 * now belongs to no layer, so those callers would silently toggle nothing.
 * Anything reaching for an imagery layer must ask, because which one is on
 * screen depends on the cursor date.
 *
 * Returns null when the entry has nothing mounted yet.
 */
export function currentImageryLayerId(map, registryId) {
    const ids = imageryLayerIdsFor(map, registryId);
    // Most-recently-shown last: that is the one sync just made current.
    return ids.length ? ids[ids.length - 1] : null;
}

/**
 * EVERY mounted layer id for a registry entry, oldest first.
 *
 * Ordering has to move all of them, not just the current one. During a date
 * change two variants are on the map at once — that overlap is what hides the
 * blank flash — and moving only one would leave the other above the AOI anchor
 * for the duration of the load, which is precisely when it is visible.
 */
export function imageryLayerIdsFor(map, registryId) {
    if (!map || !registryId) return [];
    const keys = variantsFor(map).get(registryId) ?? [];
    return keys
        .map(k => imageryIdsFor(registryId, k).layerId)
        .filter(id => map.getLayer(id));
}

/**
 * Hide the variants of `registryId` that are not `keepKey`, and drop the ones
 * past the retention window. Deferred until the incoming source has loaded so
 * the outgoing imagery stays on screen until there is something to replace it.
 */
function retireOtherVariants(map, registryId, keepKey) {
    const all = variantsFor(map);
    const keys = all.get(registryId) ?? [];
    const { sourceId: keepSource } = imageryIdsFor(registryId, keepKey);

    const apply = () => {
        if (!map.getSource(keepSource)) return;
        if (!map.isSourceLoaded(keepSource)) {
            // `idle` fires once the map settles (the retry the retired
            // useSyncFootprintLayers used for setFeatureState).
            map.once("idle", apply);
            return;
        }
        for (const key of keys) {
            if (key === keepKey) continue;
            const { layerId } = imageryIdsFor(registryId, key);
            if (map.getLayer(layerId)) setLayerVisibility(map, layerId, false);
        }
        // Evict beyond the window, oldest first, never the one on screen.
        const ordered = (all.get(registryId) ?? []).filter(k => k !== keepKey);
        while (ordered.length > RETAINED_DATES - 1) {
            const drop = ordered.shift();
            const { sourceId, layerId } = imageryIdsFor(registryId, drop);
            if (map.getLayer(layerId)) map.removeLayer(layerId);
            if (map.getSource(sourceId)) map.removeSource(sourceId);
            all.set(registryId, (all.get(registryId) ?? []).filter(k => k !== drop));
        }
    };
    apply();
}

function setLayerVisibility(map, layerId, visible) {
    if (!map.getLayer(layerId)) return;
    map.setLayoutProperty(layerId, "visibility", visible ? "visible" : "none");
}

// Hide a registry entry. For imagery that means every DATE it has mounted:
// idsFor(layer.id) names no imagery layer since sources became per-date, so
// hiding by it hid nothing and the last date stayed on screen.
function hideEntry(map, layer, layerId) {
    if (layer.kind === "imagery") {
        for (const id of imageryLayerIdsFor(map, layer.id)) {
            setLayerVisibility(map, id, false);
        }
    }
    setLayerVisibility(map, layerId, false);
}

// Push a user layer's colors onto MapLibre layers that already exist.
// syncGeoJsonLayer sets paint only when it ADDS a layer, so without this a
// color changed from the My Layers swatch saves, and the map keeps drawing the
// old color until a reload. Same fallbacks as syncGeoJsonLayer. Compared first,
// so the ordinary sync -- nothing changed -- costs a read, not a repaint.
export function syncUserLayerPaint(map, layer) {
    const sid = layer.source?.sourceId;
    if (!sid) return;
    const fallback = layer._staticColor ?? "#888780";
    const wanted = [
        [`${sid}-fill`,   "fill-color",   layer._fillColor   ?? fallback],
        [`${sid}-stroke`, "line-color",   layer._strokeColor ?? fallback],
        [`${sid}-point`,  "circle-color", layer._pointColor  ?? fallback],
    ];
    for (const [id, prop, value] of wanted) {
        if (!map.getLayer(id)) continue;
        if (JSON.stringify(map.getPaintProperty(id, prop)) !== JSON.stringify(value)) {
            map.setPaintProperty(id, prop, value);
        }
    }
}

function applySiteFilter(map, layerId, selectedSite) {
    if (!map.getLayer(layerId)) return;

    if (!selectedSite) {
        map.setFilter(layerId, null);
        return;
    }

    const geom =
        selectedSite.geom?.type === "Feature"
            ? selectedSite.geom.geometry
            : selectedSite.geom;

    if (!geom || !geom.type || !geom.coordinates) {
        map.setFilter(layerId, null);
        return;
    }

    map.setFilter(layerId, ["within", geom]);
}

export function useSyncRegistryLayers(
    mapRef,
    mapLoaded,
    registryLayers,
    addLayer,
    removeLayer,
    activeOperatingAoiId,
    selectedSite,
    paneFilter = null
) {

    const mountedImageryRef = useRef(new Set());

    useEffect(() => {
        const map = mapRef.current;
        if (!map || !mapLoaded) return;

        const layers = Object.values(registryLayers);

        const currentImageryIds = new Set(
            layers
                .filter(l => l.scope === "imagery" || l.kind === "imagery")
                .map(l => l.id)
        );

        for (const previouslyMountedId of mountedImageryRef.current) {
            if (!currentImageryIds.has(previouslyMountedId)) {
                // Every DATE this entry mounted, not just one id. Removing only
                // idsFor(id) would strand a source per date the user visited,
                // and nothing else would ever collect them.
                removeAllImageryVariants(map, previouslyMountedId);
                mountedImageryRef.current.delete(previouslyMountedId);
            }
        }

        for (const layer of layers) {
            const { sourceId, layerId } = idsFor(layer.id);

            // User layers (drawn + imported) use geojson-inline sources.
            // If the MapLibre layer already exists, handle visibility with
            // setLayoutProperty only — no source/token work needed.
            // This prevents a visibility toggle from re-running syncImageryLayer
            // for every other layer in the registry.
            if (layer.kind === "user-layer" || layer.kind === "user-layer-outline") {
                const mlId = layer.maplibre_layer?.id;
                const vis = !!layer.visible;

                // Always sync ALL known sub-layer suffixes when visibility changes.
                // This covers layers added by any previous version of buildRegistryLayers
                // (-line, -stroke, -point) so none get orphaned on toggle-off.
                if (layer.db_layer_id) {
                    const prefix = `user-layer-${layer.db_layer_id}`;
                    ["-fill", "-line", "-stroke", "-point", "-note-dot", "-note-label"].forEach(suffix => {
                        const sid = `${prefix}${suffix}`;
                        if (map.getLayer(sid)) setLayerVisibility(map, sid, vis);
                    });
                }

                // Push new content into an existing source before the
                // early-out below. Editing a saved layer replaces its
                // FeatureCollection in place — same layer id, same source id
                // — so this is the only point at which the map can learn
                // about it. Skipped when the reference is unchanged, which is
                // every ordinary sync.
                if (layer?.source?.type === "geojson-inline" && layer.source.data) {
                    const sid = layer.source.sourceId;
                    const src = sid && map.getSource(sid);
                    if (src?.setData) {
                        const revs = sourceRevisions(map);
                        if (revs.get(sid) !== layer.source.data) {
                            src.setData(layer.source.data);
                            revs.set(sid, layer.source.data);
                        }
                    }
                }

                // If the primary MapLibre layer already exists, only its colors
                // can still be out of date.
                if (mlId && map.getLayer(mlId)) {
                    syncUserLayerPaint(map, layer);
                    continue;
                }

                // Layer not on map yet — skip invisible, add visible
                if (!vis) continue;

                if (layer?.source?.type === "geojson-inline") {
                    syncGeoJsonLayer({ map, layer, addLayer });
                }
                continue;
            }

            if (!layer.visible) {
                hideEntry(map, layer, layerId);
                applySiteFilter(map, layerId, null);
                continue;
            }

            // Compare mode: hide all non-imagery layers across both panes so the
            // comparison is purely about the two imagery datasets.
            if (paneFilter?.hideNonImagery && layer.kind !== "imagery") {
                setLayerVisibility(map, layerId, false);
                continue;
            }

            if (layer.kind === "imagery") {
                // When paneFilter is set (compare mode), only sync this pane's
                // imagery: its 10 cm entry and its own reference lane (B4,
                // paneImageryIds). Other imagery layers get hidden on this map.
                if (paneFilter?.allowedImageryIds != null && !paneFilter.allowedImageryIds.has(layer.id)) {
                    hideEntry(map, layer, layerId);
                    continue;
                }
                mountedImageryRef.current.add(layer.id);
                syncImageryLayer({
                    map,
                    layer,
                    sourceId,
                    layerId,
                    addLayer,
                    activeOperatingAoiId
                });
                continue;
            }

            if (layer.kind === "analysis-detection") {
                syncAnalysisLayer({
                    map,
                    layer,
                    sourceId,
                    layerId,
                    addLayer
                });

                applySiteFilter(map, layerId, selectedSite);
                continue;
            }

            if (layer?.source?.type === "raster") {
                syncRasterLayer({
                    map,
                    layer,
                    sourceId,
                    layerId,
                    addLayer
                });
            }

            if (layer?.source?.type === "geojson-inline") {
                syncGeoJsonLayer({ map, layer, addLayer });
                continue;
            }
        }

        // The reference lane (B4, D5's draw order: 10 cm / Sentinel / basemap)
        // is placed by its group when syncImageryLayer adds it (before
        // anchor-imagery, under the 10 cm lane) and is NOT moved here: moving
        // it with imagery would lift it above the 10 cm lane.
        const imageryLayers = layers.filter(
            l => (l.scope === "imagery" || l.kind === "imagery") && l.visible && l.group !== "reference"
        );

        if (imageryLayers.length > 0) {
            // The footprint-over group's anchor: placing imagery before it keeps
            // imagery under the entitled mask and edges (footprint restyle
            // S-b), and so under AOIs. It was anchor-aois while the footprints
            // were drawn under imagery.
            const nextAnchor = FOOTPRINT_OVER_ANCHOR;
            const anchorExists = map.getLayer(nextAnchor);

            for (const layer of imageryLayers) {
                // ALL variants, resolved rather than constructed. idsFor(layer.id)
                // names no imagery layer since sources became per-date, so this
                // loop used to `continue` on every entry and imagery silently
                // stopped being ordered under its anchor.
                for (const layerId of imageryLayerIdsFor(map, layer.id)) {
                    if (anchorExists) {
                        map.moveLayer(layerId, nextAnchor);
                    } else {
                        map.moveLayer(layerId);
                    }
                }
            }
        }

    }, [mapRef, mapLoaded, registryLayers, addLayer, removeLayer, activeOperatingAoiId, selectedSite, paneFilter?.allowedImageryIds, paneFilter?.hideNonImagery]);

    useEffect(() => {
        const map = mapRef.current;
        if (!map || !mapLoaded) return;

        // Every RRC layer, so a SiteID selection narrows them consistently.
        // At two, selecting a site clipped surface wells and laterals to it
        // while injection wells and permits stayed drawn statewide — the same
        // map showing two RRC layers scoped and two not.
        //
        // NOTE, not fixed here: MapView's well-visibility effect also calls
        // setFilter on rrc-well-points and rrc-well-laterals for the status and
        // user-AOI filters, so those two layers have two writers and whichever
        // effect runs last wins. That collision predates this change and adding
        // the new ids does not widen it — the two new layers have no second
        // writer.
        const LAYERS = [
            "rrc-well-points",
            "rrc-well-laterals",
            "rrc-uic-points",
            "rrc-permit-points"
        ];

        const geom =
            selectedSite?.geom?.type === "Feature"
                ? selectedSite.geom.geometry
                : selectedSite?.geom;

        LAYERS.forEach((id) => {
            if (!map.getLayer(id)) return;

            if (!geom) {
                map.setFilter(id, null);
            } else {
                map.setFilter(id, ["within", geom]);
            }
        });

    }, [mapRef, mapLoaded, selectedSite]);
}

// Exported for its tile-URL test (useSyncRegistryLayers.tileUrl.test.js).
export async function syncImageryLayer({
    map,
    layer,
    sourceId,
    layerId,
    addLayer,
    activeOperatingAoiId
}) {
    const datasetKey = layer.source?.dataset_key;
    if (!datasetKey) return;

    // Per-DATE ids. The sourceId/layerId passed in are the per-entry shape and
    // are deliberately shadowed: keying by date is what lets the outgoing
    // imagery stay on screen while the incoming loads. See imageryIdsFor.
    ({ sourceId, layerId } = imageryIdsFor(layer.id, datasetKey));

    try {
        const { ck } = await getOrFetchTileToken(datasetKey, {
            operatingAoiId: activeOperatingAoiId ?? null
        });

        // The map can be removed while the token is fetched: Explore's compare
        // map, on leaving Compare (REM-703). A removed map is never called.
        if (!isLiveMap(map)) return;

        const current = useLayerRegistryStore.getState().layers[layer.id];
        if (!current?.visible) return;

        const tileUrl = buildTileUrl(datasetKey, ck, { operatingAoi: activeOperatingAoiId });

        const existingSource = map.getSource(sourceId);

        if (!existingSource) {
            map.addSource(sourceId, {
                type: "raster",
                tiles: [tileUrl],
                tileSize: 256,
                minzoom: layer.minzoom ?? 0,
                maxzoom: layer.maxzoom ?? 22,
                // The Copernicus credit (B4): on the reference source only, so
                // MapLibre's attribution control shows it exactly while the
                // Sentinel-2 lane is drawn. Sources are per date, so the year
                // never changes under a mounted source.
                ...(layer.group === "reference"
                    ? { attribution: copernicusAttribution(layer.meta?.effective_date) }
                    : {})
            });
        } else if (existingSource.setTiles) {
            // STILL NEEDED, but no longer for date changes — those now get their
            // own source. What reaches here is the same date with a different
            // URL: a refreshed `ck` after a token renewal, or an operating-AOI
            // switch. Both must invalidate, because the old tiles are genuinely
            // wrong. Guarded on the URL actually differing, because calling
            // setTiles with an unchanged URL re-fetches everything and produces
            // spurious decode errors while tiles are mid-load.
            const currentTiles = existingSource.tiles || [];
            if (currentTiles[0] !== tileUrl) {
                existingSource.setTiles([tileUrl]);
            }
        }

        // Track this date as mounted for the entry, most-recently-shown last.
        const variants = variantsFor(map);
        const mounted = variants.get(layer.id) ?? [];
        variants.set(layer.id, [...mounted.filter(k => k !== datasetKey), datasetKey]);

        map.__datasetKeyBySource ||= new Map();
        map.__datasetKeyBySource.set(sourceId, datasetKey);

        const desiredOpacity = layer.opacity ?? 1;
        // Per-layer raster adjustments — read off the registry entry alongside
        // opacity. Identity fallback keeps legacy/degraded entries untouched.
        const adjust = layer.adjust ?? {
            brightnessMin: 0, contrast: 0, saturation: 0, hueRotate: 0
        };

        if (!map.getLayer(layerId)) {
            addLayer({
                // "reference" for the Sentinel-2 lane (B4): under anchor-imagery.
                group: layer.group === "reference" ? "reference" : "imagery",
                layerConfig: {
                    id: layerId,
                    type: "raster",
                    source: sourceId,
                    layout: {
                        visibility: "visible"
                    },
                    paint: {
                        "raster-opacity": desiredOpacity,
                        // Seed adjustments at add-time so a re-add (AOI change, token
                        // refresh, paneFilter change) restores them — not identity.
                        "raster-brightness-min": adjust.brightnessMin,
                        "raster-contrast": adjust.contrast,
                        "raster-saturation": adjust.saturation,
                        "raster-hue-rotate": adjust.hueRotate
                    }
                }
            });
        } else {
            setLayerVisibility(map, layerId, true);
            // Sync raster-opacity when the registry's opacity has changed.
            // setPaintProperty triggers a repaint without reloading tiles, which
            // is the cheap path for opacity-only changes.
            const current = map.getPaintProperty(layerId, "raster-opacity");
            if (current !== desiredOpacity) {
                map.setPaintProperty(layerId, "raster-opacity", desiredOpacity);
            }
            // Same getPaintProperty/compare/setPaintProperty pattern per adjust
            // prop, mirroring opacity. setPaintProperty repaints in place — no
            // tile reload — and re-seeding on re-add means non-identity values
            // survive instead of snapping back.
            const curBrightness = map.getPaintProperty(layerId, "raster-brightness-min");
            if (curBrightness !== adjust.brightnessMin) {
                map.setPaintProperty(layerId, "raster-brightness-min", adjust.brightnessMin);
            }
            const curContrast = map.getPaintProperty(layerId, "raster-contrast");
            if (curContrast !== adjust.contrast) {
                map.setPaintProperty(layerId, "raster-contrast", adjust.contrast);
            }
            const curSaturation = map.getPaintProperty(layerId, "raster-saturation");
            if (curSaturation !== adjust.saturation) {
                map.setPaintProperty(layerId, "raster-saturation", adjust.saturation);
            }
            const curHue = map.getPaintProperty(layerId, "raster-hue-rotate");
            if (curHue !== adjust.hueRotate) {
                map.setPaintProperty(layerId, "raster-hue-rotate", adjust.hueRotate);
            }
        }

        // Hide the date we just left, but only once THIS one has tiles. Doing
        // it here rather than above is the whole point: for the duration of the
        // load the new layer is empty and the old one shows through, so the map
        // never goes blank between acquisitions.
        retireOtherVariants(map, layer.id, datasetKey);
    } catch (err) {
        console.error("[imagery] sync failed:", err);
    }
}

function syncAnalysisLayer({
    map,
    layer,
    sourceId,
    layerId,
    addLayer
}) {
    const tileUrl = layer.source?.tile_url;
    const sourceLayer = layer.source?.source_layer;

    if (!tileUrl || !sourceLayer) return;

    if (!map.getSource(sourceId)) {
        map.addSource(sourceId, {
            type: "vector",
            tiles: [`${window.location.origin}${tileUrl}`],
            minzoom: 0,
            maxzoom: 22
        });
    }

    if (!map.getLayer(layerId)) {
        addLayer({
            group: "asset",
            layerConfig: {
                id: layerId,
                type: "fill",
                source: sourceId,
                "source-layer": sourceLayer,
                filter: layer.filter ?? undefined,
                layout: {
                    visibility: "visible"
                },
                paint: {
                    "fill-color": [
                        "interpolate",
                        ["linear"],
                        ["get", "confidence"],
                        0.0, "#ff0000",
                        0.8, "#f59e0b",
                        0.95, "#22c55e"
                    ],
                    "fill-opacity": 0.08,
                    "fill-outline-color": "#111827"
                }
            }
        });
    } else {
        setLayerVisibility(map, layerId, true);
    }
}

function syncRasterLayer({
    map,
    layer,
    sourceId,
    layerId,
    addLayer
}) {
    const tileUrl = layer.source?.tile_url;
    if (!tileUrl) return;

    const existingSource = map.getSource(sourceId);

    if (!existingSource) {
        map.addSource(sourceId, {
            type: "raster",
            tiles: [tileUrl],
            tileSize: 256,
            minzoom: layer.minzoom ?? 0,
            maxzoom: layer.maxzoom ?? 22
        });
    } else if (existingSource.setTiles) {
        existingSource.setTiles([tileUrl]);
    }

    if (!map.getLayer(layerId)) {
        addLayer({
            group: "imagery",
            layerConfig: {
                id: layerId,
                type: "raster",
                source: sourceId,
                layout: {
                    visibility: "visible"
                },
                paint: {
                    "raster-opacity": 1
                }
            }
        });
    } else {
        setLayerVisibility(map, layerId, true);
    }
}

function syncGeoJsonLayer({ map, layer, addLayer }) {
    const sourceId = layer.source?.sourceId;
    const maplibre_layer = layer.maplibre_layer;
    if (!sourceId || !maplibre_layer) return;

    // Add GeoJSON source if not already present. Record what it was created
    // with, so the revision check above starts from the truth and does not
    // re-push every layer on the first sync after load.
    if (!map.getSource(sourceId)) {
        map.addSource(sourceId, {
            type: "geojson",
            data: layer.source.data,
        });
        sourceRevisions(map).set(sourceId, layer.source.data);
    }

    // Primary fill layer (Polygon only)
    if (!map.getLayer(maplibre_layer.id)) {
        addLayer({
            group: "user",
            layerConfig: { ...maplibre_layer, layout: { visibility: "visible" } },
        });
    } else {
        setLayerVisibility(map, maplibre_layer.id, true);
    }

    // Stroke — Polygon outlines + LineStrings
    const strokeId = `${sourceId}-stroke`;
    const strokeColor = layer._strokeColor ?? layer._staticColor ?? "#888780";
    if (!map.getLayer(strokeId)) {
        addLayer({
            group: "user",
            layerConfig: {
                id: strokeId,
                type: "line",
                source: sourceId,
                filter: ["in", ["geometry-type"], ["literal", ["Polygon", "LineString"]]],
                layout: { visibility: "visible" },
                paint: {
                    "line-color": strokeColor,
                    "line-width": 2,
                    "line-opacity": 0.9,
                },
            },
        });
    } else {
        setLayerVisibility(map, strokeId, true);
    }

    // Points — circles for Point geometry

    const pointId = `${sourceId}-point`;
    const pointColor = layer._pointColor ?? layer._staticColor ?? "#888780";  // ← ADD THIS
    if (!map.getLayer(pointId)) {
        addLayer({
            group: "user",
            layerConfig: {
                id: pointId,
                type: "circle",
                source: sourceId,
                filter: ["all",
                    ["==", ["geometry-type"], "Point"],
                    ["!=", ["get", "annotationType"], "note"],
                ],
                layout: { visibility: "visible" },
                paint: {
                    "circle-radius": 6,
                    "circle-color": pointColor,
                    "circle-opacity": 0.9,
                    "circle-stroke-color": "#000",
                    "circle-stroke-width": 1.5,
                    "circle-stroke-opacity": 0.6,
                },
            },
        });
    } else {
        setLayerVisibility(map, pointId, true);
    }

    // ── NO BUBBLE ON A SAVED NOTE EITHER ────────────────────────────────
    //
    // The drawing session drops the 🗨 (useAnnotationLayer). This is the OTHER
    // half: the layer a note is rendered from once it has been saved to My
    // Layers. Removing only the first would have taken the bubble away while
    // drawing and handed it back the moment the layer was saved — a change that
    // looks done and is not, and reads as a rendering bug rather than a choice.
    //
    // The text carries everything the glyph did: it marks the same coordinate,
    // and it says what the note actually is.

    const noteId = `${sourceId}-note-label`;
    if (!map.getLayer(noteId)) {
        addLayer({
            group: "user",
            layerConfig: {
                id: noteId,
                type: "symbol",
                source: sourceId,
                filter: ["==", ["get", "annotationType"], "note"],
                layout: {
                    "text-field": ["get", "label"],
                    "text-font": ["Open Sans Regular", "Arial Unicode MS Regular"],
                    "text-size": 13,
                    // Centred on the coordinate now that no glyph sits there to
                    // clear. Was [0,-1.6]/bottom, which floated the text above a
                    // bubble that no longer exists.
                    "text-offset": [0, 0],
                    "text-anchor": "center",
                    "text-max-width": 12,
                    // Was false: a note dropping out under label collision was
                    // survivable while a bubble still marked the spot, and is an
                    // annotation vanishing now.
                    "text-allow-overlap": true,
                    "icon-allow-overlap": true,
                },
                paint: {
                    "text-color": "#ffffff",
                    "text-halo-color": "rgba(15,23,42,0.85)",   // slate-900
                    "text-halo-width": 3,
                    "text-halo-blur": 0,                         // zero blur = hard edge = box-like
                },
            },
        });
    } else {
        // `vis` was the caller's local (the user-layer branch of the sync
        // loop), not anything in scope here — so reaching this line threw a
        // ReferenceError rather than setting visibility. Same value, read off
        // the layer this function was actually handed.
        setLayerVisibility(map, noteId, layer?.visible ?? true);
    }
}